import Edit from "@mui/icons-material/Edit";
import { Skeleton, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useState, type FC, type KeyboardEvent } from "react";
import type { ErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import { ChannelIcon } from "../../channels/components/channel-icon";
import type { ChannelName } from "../../channels/types";
import type { MomentStatus } from "../../moment-card/types";
import { statusToPill } from "../../moment-card/utils";

interface HeaderBarTitleProps {
  title:
    | { type: "fixed"; value: string }
    | { type: "editable"; value: string; onChange: (newValue: string) => void; error: ErrorProps };
  channel?: ChannelName;
  disabled?: boolean;
  subtitle?: string;
  status?: MomentStatus;
  loading?: boolean;
}

export const HeaderBarTitle: FC<HeaderBarTitleProps> = ({
  title,
  subtitle,
  status,
  channel,
  disabled = false,
  loading = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title.value);

  const handleClick = useCallback(() => {
    setDraftTitle(title.value);
    setEditing(true);
  }, [title.value]);

  const save = useCallback(() => {
    if (title.type !== "editable") return setEditing(false);

    const trimmed = draftTitle.trim();
    if (trimmed) {
      title.onChange(trimmed);
    }
    setEditing(false);
  }, [draftTitle, title]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        save();
      } else if (event.key === "Escape") {
        setEditing(false);
      }
    },
    [save],
  );

  const editable = title.type === "editable" && !disabled;

  return (
    <Stack direction="column" justifyContent="center">
      <Stack direction="row" spacing={1} alignItems="center">
        {channel && <ChannelIcon channel={channel} />}
        {!loading && editing && title.type === "editable" && (
          <TextField
            size="small"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            variant="outlined"
            error={title.error.error}
            helperText={title.error?.helperText}
            disabled={disabled}
          />
        )}

        {!loading && !editing && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body1"
              component="h1"
              sx={{ fontWeight: "bold" }}
              onClick={editable ? handleClick : undefined}
            >
              {title.value}
            </Typography>
            {editable && (
              <Edit
                data-analytics-id="header-bar-title-edit-icon"
                data-testid="header-bar-title-edit-icon"
                fontSize="small"
                sx={{ fontWeight: 100, fontSize: 12, cursor: "pointer" }}
                onClick={handleClick}
              />
            )}
          </Stack>
        )}
        {loading && <Skeleton variant="text" width={150} height={30} role="progressbar" />}
        {status && statusToPill(status)}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption">{subtitle ?? "\xa0"}</Typography>
      </Stack>
    </Stack>
  );
};
